import {
  SelectProps as MantineSelectProps,
  Select as MantineSelect,
  ComboboxData,
  Group,
  Text,
  ComboboxLikeRenderOptionInput,
  ComboboxItem,
  Stack,
  Box,
} from "@mantine/core"
import { useUncontrolled } from "@mantine/hooks"
import { IconPlus } from "@tabler/icons-react"
import { forwardRef, ReactNode, useEffect, useState } from "react"

import { CopyButton } from "../../Buttons/CopyButton/CopyButton.tsx"
import { Loader } from "../../Loader/Loader.tsx"

import styles from "./Select.module.css"

const DefaultNothingFoundMessage = forwardRef<
  HTMLDivElement,
  { searchValue?: string; label: string; onClick?: (query?: string) => void }
>(({ searchValue, label, onClick }, ref) => (
  <Group
    ref={ref}
    className={styles.createLabel}
    onClick={() => onClick && onClick(searchValue)}
  >
    <IconPlus size={16} height={16} color="var(--mantine-color-green-6)" />
    <Text>{searchValue ? `${label} (${searchValue})` : <>{label}</>}</Text>
  </Group>
))

export type SelectProps = {
  creatable?: boolean
  isLoading?: boolean
  createLabel?: string
  onCreate?: (query?: string) => void
  nothingFoundMessage?: string | ReactNode
  copyableValue?: string
  isAdmin?: boolean
  data?: ComboboxData
  customRender?: (
    item: ComboboxLikeRenderOptionInput<ComboboxItem>
  ) => ReactNode
  errorOnTouch?: boolean
} & Omit<MantineSelectProps, "renderOption">

export const Select = forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      data,
      value,
      isAdmin,
      defaultValue,
      searchValue,
      defaultSearchValue,
      onSearchChange,
      creatable,
      copyableValue,
      createLabel = "Create",
      nothingFoundMessage,
      onCreate,
      customRender,
      onChange,
      isLoading,
      error,
      errorOnTouch = true,
      ...props
    },
    ref
  ) => {
    const [selectOptions, setSelectOptions] = useState<
      ComboboxData | undefined
    >(data)
    useEffect(() => {
      setSelectOptions(
        data &&
          creatable &&
          !data.some(
            (item) =>
              (typeof item === "object" &&
                "value" in item &&
                item.value === "$create") ||
              item === "$create"
          )
          ? [...data, { value: "$create", label: "$create" }]
          : data
      )
    }, [data, creatable])
    const [touched, setTouched] = useState(false)
    const [_value, setValue, controlled] = useUncontrolled({
      value,
      defaultValue,
      finalValue: null,
      onChange,
    })
    const [_searchValue, setSearchValue] = useUncontrolled({
      value: searchValue,
      defaultValue: defaultSearchValue,
      finalValue: getFinalValue(value),
      onChange: onSearchChange,
    })

    const createOption = (
      <Group
        gap="var(--tfg-molecule-padding-x)"
        wrap="nowrap"
        className={styles.createLabel}
      >
        <IconPlus size={16} color="var(--mantine-color-primary-6)" />
        <Text c="primary" fw={700}>
          {createLabel}
        </Text>
      </Group>
    )

    const defaultRenderOption = ({
      option,
      checked,
    }: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
      return (
        <Box
          className={styles.optionRender}
          p="var(--tfg-molecule-padding-y)"
          data-checked={checked ? "true" : undefined}
        >
          <Text>{option.label}</Text>
        </Box>
      )
    }

    const renderOption = ({
      option,
      checked,
    }: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
      if (option.value === "$create") {
        return createOption
      }
      return customRender
        ? customRender({ option, checked })
        : defaultRenderOption({ option, checked })
    }
    const content = (
      <>
        {copyableValue && value && (
          <Group h="fit-content">
            <CopyButton
              label={copyableValue}
              copyableText={value}
              size="xs"
              p={0}
              admin={isAdmin}
              variant={isAdmin ? "admin-tertiary" : "tertiary"}
            />
          </Group>
        )}
        <MantineSelect
          ref={ref}
          data={selectOptions}
          classNames={styles}
          value={_value}
          onChange={(value, option) => {
            setTouched(true)

            if (value && value !== "$create") {
              setValue(value, option)
              !controlled && setSearchValue(value ?? "")
            } else if (value === "$create") {
              if (
                !data?.some(
                  (item) =>
                    typeof item === "object" &&
                    "label" in item &&
                    item.label.toLowerCase() === _searchValue?.toLowerCase()
                )
              ) {
                onCreate?.(_searchValue)
              }
              setSearchValue("")
            }
          }}
          onSearchChange={(value) => {
            setSearchValue(value)
          }}
          onBlur={() => {
            setTouched(true)
          }}
          onClear={() => {
            setTouched(true)
            {
              setValue(null, { value: "", label: "" })
            }
          }}
          nothingFoundMessage={
            nothingFoundMessage ? (
              nothingFoundMessage
            ) : creatable ? (
              <DefaultNothingFoundMessage
                searchValue={_searchValue}
                label={createLabel || `Create (${_searchValue})`}
                onClick={() => {
                  onCreate && onCreate(_searchValue)
                  setSearchValue("")
                }}
              />
            ) : null
          }
          rightSection={isLoading ? <Loader type="bars" size={16} /> : null}
          searchValue={_searchValue}
          renderOption={renderOption}
          error={errorOnTouch ? touched && error : error}
          {...props}
        />
      </>
    )

    return copyableValue ? (
      <Stack gap="var(--tfg-molecule-padding-y)">{content}</Stack>
    ) : (
      content
    )
  }
)

Select.displayName = "Select"

function getFinalValue(
  value: string | { label: string } | null | undefined
): string {
  return typeof value === "string" ? value : (value?.label ?? "")
}
